import { gql, useMutation } from "@apollo/client";
import { faChevronDown, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useApplicationContext } from "components/Application";
import { CartItem } from "components/CartItem";
import { capitalize, keys, map } from "lodash";
import numeral from "numeral";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import tw from "twin.macro";

const confirmOrderDocumentMutation = gql`
  mutation ($input: ConfirmOrderDocumentInput!) {
    confirmOrderDocument(input: $input) {
      document {
        id
      }
    }
  }
`;

const updateNotesMutation = gql`
  mutation ($id: ID!, $notes: String) {
    updateOrderDocumentNotes(id: $id, notes: $notes) {
      document {
        id
      }
    }
  }
`;

type CartRowsProps = {
  promoQty: number;
  document: any;
  brandId: string;
  refetch: any;
  stock: boolean;
  shipping: string;
};

export const CartRows: React.FC<CartRowsProps> = (props) => {
  const { document, brandId, refetch, stock, shipping, promoQty } = props;
  const { rows } = document;

  const { session } = useApplicationContext();
  const { currentUser } = session;
  const { priceList } = currentUser;
  const { prices } = priceList;
  const navigate = useNavigate();

  const { i18n } = useTranslation();
  const languageSuffix = capitalize(i18n.language);

  const { register, handleSubmit } = useForm();

  const [dirtyRows, setDirtyRows] = useState<string[]>([]);

  const rowGroups = useMemo<{ [key: string]: any[] }>(() => {
    const rowGroups: { [key: string]: any[] } = {};
    rows.forEach((row: any) => {
      rowGroups[row.referenceItem.id] = [];
    });
    rows.forEach((row: any) => {
      rowGroups[row.referenceItem.id].push({ ...row, price: 0 });
    });
    return rowGroups;
  }, [rows]);

  const externalCompositions = useMemo<{ [key: string]: number }>(() => {
    const externalCompositions: { [key: string]: number } = {};
    rows.forEach((row: any) => {
      const id = row.referenceItem.externalComposition.id;
      let qty = 0;
      row.sizeQuantities?.forEach((sizeQuantity: any) => (qty += sizeQuantity.quantity));
      id in externalCompositions ? (externalCompositions[id] += qty) : (externalCompositions[id] = qty);
    });
    return externalCompositions;
  }, [rows]);

  const maxSizes = useMemo<number>(() => {
    let maxSizes = 0;
    rows.forEach((row: any) => {
      if (row.referenceItem.sizeGroup.sizes.length > maxSizes) maxSizes = row.referenceItem.sizeGroup.sizes.length;
    });
    return maxSizes;
  }, [rows]);

  const [execConfirm, { loading }] = useMutation(confirmOrderDocumentMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      console.error(graphQLErrors);
    },
    onCompleted: () => {
      toast.success(i18n.language === "it" ? "Ordine confermato correttamente!" : "Order successfully confirmed!", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      refetch();
      navigate("/cart/confirmed");
    },
  });

  const onSubmit = (data: any) => {
    const rows = document.rows.map((row: any) => row.id);
    execConfirm({ variables: { input: { ...data, userId: currentUser.id, brandId, rows } } });
  };

  const [totalQuantity, total, taxes, grandTotal] = useMemo<[number, number, number, number]>(() => {
    let totalQuantity = 0;
    let total = 0;
    map(rowGroups, (rowGroup: any) =>
      rowGroup.forEach((row: any) => {
        let rowTotalQuantity = 0;
        row.sizeQuantities?.forEach((sizeQuantity: any) => (rowTotalQuantity += sizeQuantity.quantity));
        let price: any | false = false;
        prices.forEach((p: any) => {
          const item = row.referenceItem;
          if (p.itemId === item.id)
            price =
              p.wholesalePromo > 0 && externalCompositions[item.externalComposition.id] >= promoQty
                ? p.wholesalePromo
                : p.wholesale;
        });
        row.price = price;
        totalQuantity += rowTotalQuantity;
        total += price * rowTotalQuantity;
      }),
    );

    let vatValue = 0;
    if (currentUser.priceList.code === "ITA") {
      vatValue = 0.22;
    }
    const taxes = total * vatValue;
    const grandTotal = total + taxes;
    return [totalQuantity, total, taxes, grandTotal];
  }, [rowGroups, prices, currentUser.priceList.code, externalCompositions, promoQty]);

  const isDirty = dirtyRows.length > 0;

  const salesTermsFilePath = "/sales_terms_" + languageSuffix + ".pdf";

  const [notes, setNotes] = useState(document.notes);

  const [notesMutation] = useMutation(updateNotesMutation, {});

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) initialRender.current = false;
    else {
      const timeout = setTimeout(() => notesMutation({ variables: { id: document.id, notes } }), 1000);
      return () => clearTimeout(timeout);
    }
  }, [notes, notesMutation, document.id]);

  return (
    <div tw="flex-col lg:w-auto w-screen" style={{ margin: "4rem auto" }}>
      <div tw="mb-16 w-auto">
        {keys(rowGroups).length > 0 ? (
          map(rowGroups, (rowGroup: any, index: number) => (
            <CartItem
              key={index}
              document={document}
              rowGroup={rowGroup}
              maxSizes={maxSizes}
              refetch={refetch}
              dirtyRows={dirtyRows}
              setDirtyRows={setDirtyRows}
              stock={stock}
            />
          ))
        ) : (
          <div tw="flex justify-between w-auto mt-64 mb-64">
            <div tw="w-auto text-center">
              {i18n.language === "it" ? "Nessun articolo nel carrello" : "No items in your selection"}
            </div>
          </div>
        )}
      </div>
      {keys(rowGroups).length > 0 && (
        <Fragment>
          <div tw="flex justify-between lg:w-auto w-screen lg:mx-auto mx-0 lg:px-0 px-2 border-t-default border-gray-900 pt-8">
            <div tw="flex-grow-default">
              <strong>{i18n.language === "it" ? "RIEPILOGO ORDINE" : "ORDER SUMMARY"}</strong>
            </div>
            <div tw="w-48 text-right leading-8">
              <strong>{i18n.language === "it" ? "Pezzi" : "Quantity"}</strong>
              <br />
              {totalQuantity}
            </div>
            <div tw="w-48 text-right leading-8">
              <strong>{i18n.language === "it" ? "Imponibile" : "Total"}</strong>
              <br />
              {numeral(total).format("$0,0.00")}
            </div>
            <div tw="w-48 text-right leading-8">
              <strong>{i18n.language === "it" ? "Imposta" : "Taxes"}</strong>
              <br />
              {numeral(taxes).format("$0,0.00")}
            </div>
            <div tw="w-48 text-right leading-8">
              <strong>{i18n.language === "it" ? "Tot. Ordine" : "Grand Total"}</strong>
              <br />
              {numeral(grandTotal).format("$0,0.00")}
            </div>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <input type="hidden" name="paymentMethodId" defaultValue={document.paymentMethod.id} ref={register} />
            <input type="hidden" name="seasonKind" defaultValue={stock ? "stock" : "current"} ref={register} />
            <div tw="flex justify-end items-center lg:w-auto w-screen lg:mx-auto mx-0 lg:px-0 px-2 border-gray-900 mt-6">
              <p tw="mr-1">
                <input tw="mr-4" type="checkbox" required name="salesTerms"></input>
                {i18n.language === "it" ? "Accetto le" : "I Accept"}{" "}
                <Link target="_blank" to={salesTermsFilePath} tw="leading-10 underline">
                  {i18n.language === "it" ? "Condizioni di Vendita" : "the Sales Terms"}
                  <FontAwesomeIcon icon={faFilePdf} tw="ml-2" />
                </Link>
              </p>
            </div>
            <div tw="flex justify-between space-x-4 lg:w-auto w-screen lg:mx-auto mx-0 lg:px-0 px-2 border-gray-900  ">
              <p>
                <span tw="block leading-10">{i18n.language === "it" ? "METODO DI PAGAMENTO" : "PAYMENT METHOD"}</span>
                <span tw="block border-default border-gray-500 bg-gray-200 text-gray-700 p-2 cursor-not-allowed">
                  {document.paymentMethod[`name${languageSuffix}`]}
                  <FontAwesomeIcon icon={faChevronDown} tw="ml-4" />
                </span>
              </p>
              <p>
                <span tw="block leading-10">{i18n.language === "it" ? "PERIODO DI CONSEGNA" : "DELIVERY WINDOW"}</span>
                <span tw="block border-default border-gray-500 bg-gray-200 text-gray-700 p-2 cursor-not-allowed">
                  {!stock
                    ? shipping
                    : i18n.language === "it"
                    ? "Entro 10gg dalla conferma"
                    : "10 Days from confirmation"}
                </span>
              </p>
              <p tw="flex-grow-default">
                <span tw="block leading-10">{i18n.language === "it" ? "NOTE" : "NOTES"}</span>
                <textarea
                  tw="border-default border-gray-900 text-gray-900 lg:py-2 lg:px-2 py-5 px-2 "
                  style={{ width: "100%" }}
                  rows={1}
                  name="notes"
                  defaultValue={document.notes}
                  onChange={(e) => setNotes(e.target.value)}
                  ref={register}
                ></textarea>
              </p>
              <p tw="block">
                <span tw="block leading-10">&nbsp;</span>
                <button
                  css={[
                    isDirty && tw`px-4 lg:py-0 py-2 border-default border-gray-500 text-gray-500 cursor-not-allowed`,
                    !isDirty &&
                      tw`px-4 lg:py-0 py-2 border-default border-gray-900 text-gray-900 hover:(bg-green-500 border-green-500 text-white)`,
                  ]}
                  style={{ minHeight: "2.5rem" }}
                  disabled={isDirty || loading}
                  type="submit"
                >
                  {i18n.language === "it" ? "CONFERMA ORDINE" : "CONFIRM ORDER"}
                </button>
              </p>
            </div>
            <div tw="flex justify-center space-x-4 lg:w-auto lg:mx-auto mx-0 lg:px-0 px-2 mb-12 border-gray-900">
              <p>
                {stock &&
                  (i18n.language === "it"
                    ? "La disponibilità dei quantitativi verranno definitivamente confermati dall’azienda dopo la verifica della giacenza fisica a magazzino."
                    : "Stock availability will be definitively confirmed by the company after checking the physical items.")}
              </p>
            </div>
          </form>
        </Fragment>
      )}
    </div>
  );
};
